import { Autocomplete, FormControlLabel, InputAdornment, Switch, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useState } from "react";
import { TaskPriority, TaskStatus } from "../../Models/Models";
import { Vocabulary } from "../../Utils/Vocabulary";
import {
  TASK_PRIORITY_OPTIONS,
  TASK_STATUS_OPTIONS,
} from "../../Utils/tasksConstants";
import styles from "../../Styles/tasksDashboard.module.css";

type Option = { value: string; label: string };
type UserOption = { id: number; label: string };

export type BacklogFilters = {
  search: string;
  status: TaskStatus | "";
  priority: TaskPriority | "";
  dateFrom: string;
  dateTo: string;
  overdue: boolean;
  assigneeId: number | "";
};

function getDefaultStartDate(): string {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return d.toISOString().split("T")[0];
}

function getDefaultEndDate(): string {
  return new Date().toISOString().split("T")[0];
}

export const BACKLOG_FILTERS_DEFAULT: BacklogFilters = {
  search: "",
  status: "",
  priority: "",
  dateFrom: getDefaultStartDate(),
  dateTo: getDefaultEndDate(),
  overdue: false,
  assigneeId: "",
};

type Props = {
  filters: BacklogFilters;
  onChange: (filters: BacklogFilters) => void;
  activeTab?: number;
  assigneeOptions?: UserOption[];
};

export default function TasksBacklogFilters({ filters, onChange, activeTab, assigneeOptions = [] }: Props) {
  const [searchInput, setSearchInput] = useState(filters.search);
  const selectedStatus = TASK_STATUS_OPTIONS.find((o) => o.value === filters.status) ?? null;
  const selectedPriority = TASK_PRIORITY_OPTIONS.find((o) => o.value === filters.priority) ?? null;
  const selectedAssignee = assigneeOptions.find((o) => o.id === filters.assigneeId) ?? null;

  function commitSearch() {
    onChange({ ...filters, search: searchInput });
  }

  return (
    <div className={styles.backlogFiltersWrapper}>
      <div className={styles.backlogToolbar}>
        <TextField
          size="small"
          placeholder={`${Vocabulary.search}…`}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && commitSearch()}
          onBlur={commitSearch}
          className={styles.backlogSearchInput}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon style={{ fontSize: 18, color: "#9ca3af" }} />
              </InputAdornment>
            ),
          }}
        />

        <Autocomplete
          size="small"
          options={TASK_STATUS_OPTIONS}
          getOptionLabel={(o: Option) => o.label}
          isOptionEqualToValue={(o, v) => o.value === v.value}
          value={selectedStatus}
          onChange={(_, v: Option | null) =>
            onChange({ ...filters, status: (v?.value ?? "") as TaskStatus | "" })
          }
          className={styles.backlogStatusSelect}
          renderInput={(params) => (
            <TextField {...params} label={Vocabulary.taskStatus} />
          )}
        />

        <Autocomplete
          size="small"
          options={TASK_PRIORITY_OPTIONS}
          getOptionLabel={(o: Option) => o.label}
          isOptionEqualToValue={(o, v) => o.value === v.value}
          value={selectedPriority}
          onChange={(_, v: Option | null) =>
            onChange({ ...filters, priority: (v?.value ?? "") as TaskPriority | "" })
          }
          className={styles.backlogFilterSelect}
          renderInput={(params) => (
            <TextField {...params} label={Vocabulary.taskPriority} />
          )}
        />

        <TextField
          size="small"
          type="date"
          label={Vocabulary.taskStartDate}
          value={filters.dateFrom}
          onChange={(e) => onChange({ ...filters, dateFrom: e.target.value })}
          className={styles.backlogFilterSelect}
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: filters.dateTo || undefined }}
        />

        <TextField
          size="small"
          type="date"
          label={Vocabulary.taskEndDate}
          value={filters.dateTo}
          onChange={(e) => onChange({ ...filters, dateTo: e.target.value })}
          className={styles.backlogFilterSelect}
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: filters.dateFrom || undefined }}
        />

        {activeTab === 1 && assigneeOptions.length > 0 && (
          <Autocomplete
            size="small"
            options={assigneeOptions}
            getOptionLabel={(o: UserOption) => o.label}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            value={selectedAssignee}
            onChange={(_, v: UserOption | null) =>
              onChange({ ...filters, assigneeId: v?.id ?? "" })
            }
            className={styles.backlogFilterSelect}
            renderInput={(params) => (
              <TextField {...params} label={Vocabulary.taskResponsible} />
            )}
          />
        )}

        <FormControlLabel
          control={
            <Switch
              checked={filters.overdue}
              onChange={(e) => onChange({ ...filters, overdue: e.target.checked })}
              size="small"
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": { color: "#e74c3c" },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#e74c3c" },
              }}
            />
          }
          label={Vocabulary.tasksOverdueFilter}
          className={styles.overdueSwitch}
        />
      </div>
    </div>
  );
}
